<template>
  <div class="driveby-page">
    <!-- Map component replaces the bg-section -->
    <DriveByRouteMap
        class="bg-section"
        :selected-route="selectedRoute"
        :devices="mapDevices"
        :selected-devices="selectedDevices"
        @devices-selected="handleMapDevicesSelected"
    />

    <!-- Start Route Overlay -->
    <StartDriveByOverlay
        :is-visible="showStartRouteOverlay"
        :route="routeToStart"
        :mode="overlayMode"
        @close="closeStartRouteOverlay"
        @route-started="handleRouteStarted"
    />

    <AppHeader title="Drive by" />
    <div class="content">
      <!-- Tabs Navigation -->
      <div class="tabs-container">
        <div class="tabs-nav">
          <button
              v-for="tab in tabs"
              :key="tab.id"
              :class="['tab-button', { active: activeTab === tab.id }]"
              @click="setActiveTab(tab.id)"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <!-- Routes Tab Content -->
      <div v-if="activeTab === 'routes'" class="tab-content">
        <div class="table-wrapper">
          <!-- Routes Table -->
          <div class="routes-table">
            <!-- Table Header -->
            <div class="table-header">
              <div
                  v-for="column in routeColumns"
                  :key="column.id"
                  class="table-cell"
                  :class="{ 'sortable': column.sortable }"
                  @click="column.sortable ? handleSort(column.id) : null"
              >
                {{ column.label }}
                <span v-if="sortColumn === column.id" class="sort-indicator">
                  {{ sortDirection === 'asc' ? '▲' : '▼' }}
                </span>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="isLoading" class="loading-state">
              <div class="loading-spinner"></div>
              <span>Loading routes data...</span>
            </div>

            <!-- No Data State -->
            <div v-else-if="!routes || routes.length === 0" class="no-data-message">
              No routes found. Create a new route to get started.
            </div>

            <!-- Table Rows -->
            <div v-else>
              <div
                  v-for="(route, index) in paginatedRoutes"
                  :key="route.id || index"
                  class="table-row"
                  @click="selectRoute(route)"
                  :class="{ 'selected': selectedRoute && selectedRoute.id === route.id }"
              >
                <div v-for="column in routeColumns" :key="column.id" class="table-cell">
                  <!-- State Column with Icon -->
                  <template v-if="column.id === 'state'">
                    <div
                        class="state-icon-wrapper"
                        :class="getStateClass(route.state)"
                        v-html="getStateIcon(route.state)"
                    ></div>
                  </template>
                  <!-- Action Column -->
                  <template v-else-if="column.id === 'action'">
                    <div class="action-buttons">
                      <button
                          class="action-button-small"
                          @click.stop="activateRoute(route)"
                          :title="route.state !== 'Template' ? `Route must be in Template state to activate (current: ${route.state})` : 'Activate Route'"
                          :disabled="route.state !== 'Template'"
                          :class="{ 'disabled-button': route.state !== 'Template' }"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                        </svg>
                      </button>
                      <button
                          class="action-button-small"
                          @click.stop="deleteRoute(route)"
                          title="Delete Route"
                          v-if="route.state !== 'Active'"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </template>
                  <!-- Other Columns -->
                  <template v-else>
                    {{ getColumnValue(route, column) }}
                  </template>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="routes.length > 0" class="pagination-controls">
            <div class="pagination-info">
              Showing {{ startItem }}-{{ endItem }} of {{ totalItems }} items
            </div>
            <div class="pagination-buttons">
              <button
                  class="pagination-button"
                  :disabled="currentPage === 1"
                  @click="changePage(currentPage - 1)"
              >
                Previous
              </button>

              <div class="page-numbers">
                <button
                    v-for="page in visiblePageNumbers"
                    :key="page"
                    :class="['page-number', { active: currentPage === page }]"
                    @click="changePage(page)"
                >
                  {{ page }}
                </button>
              </div>

              <button
                  class="pagination-button"
                  :disabled="currentPage === totalPages"
                  @click="changePage(currentPage + 1)"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Create Route Tab Content -->
      <div v-else-if="activeTab === 'createroute'" class="tab-content">
        <div class="table-wrapper">
          <!-- Combined Route Name and Filters Section -->
          <div class="route-filters-row">
            <!-- Route Name Section -->
            <div class="route-name-section">
              <h2 class="section-title">ROUTE NAME :</h2>
              <div class="search-input-container">
                <input
                    type="text"
                    v-model="newRoute.routeName"
                    placeholder="Enter route name..."
                    class="search-input"
                >
              </div>
            </div>

            <!-- Filters Section -->
            <div class="filters-section">
              <h2 class="section-title">FILTERS :</h2>
              <div class="filter-controls">
                <div class="filter-options">
                  <button
                      :class="['filter-option', { active: deviceFilters.city }]"
                      @click="focusCityFilter"
                  >
                    CITY
                  </button>
                  <button
                      :class="['filter-option', { active: deviceFilters.daysSinceReading }]"
                      @click="focusDaysFilter"
                  >
                    DAYS SINCE READING
                  </button>
                </div>

                <!-- Filter Inputs -->
                <div class="filter-inputs">
                  <div class="search-input-container" v-if="showCityFilter">
                    <input
                        ref="cityFilter"
                        type="text"
                        v-model="deviceFilters.city"
                        placeholder="Filter by city..."
                        class="search-input"
                        @input="applyFilters"
                    >
                  </div>
                  <div class="search-input-container" v-if="showDaysFilter">
                    <input
                        ref="daysFilter"
                        type="number"
                        v-model.number="deviceFilters.daysSinceReading"
                        placeholder="Days since last reading..."
                        class="search-input"
                        min="0"
                        @input="applyFilters"
                    >
                  </div>
                  <button
                      v-if="showCityFilter || showDaysFilter"
                      class="clear-filter-button"
                      @click="clearFilters"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Available Devices Table -->
          <div class="meters-table selectable-meters">
            <div class="table-actions">
              <div class="selection-actions">
                <button
                    class="action-button"
                    @click="selectAllDevices"
                    :disabled="filteredDevices.length === 0"
                >
                  Select All
                </button>
                <button
                    class="action-button"
                    @click="deselectAllDevices"
                    :disabled="selectedDevices.length === 0"
                >
                  Clear Selection
                </button>
              </div>
            </div>

            <!-- Table Header -->
            <div class="table-header">
              <div class="table-cell">LDN</div>
              <div class="table-cell">MPID</div>
              <div class="table-cell">CITY</div>
              <div class="table-cell">STREET</div>
              <div class="table-cell">LAST READING</div>
            </div>

            <!-- Loading State -->
            <div v-if="devicesLoading" class="loading-state">
              <div class="loading-spinner"></div>
              <span>Loading available devices...</span>
            </div>

            <!-- No Data State -->
            <div v-else-if="filteredDevices.length === 0" class="no-data-message">
              No devices found matching your criteria.
            </div>

            <!-- Error State -->
            <div v-else-if="devicesError" class="error-state">
              <div class="error-message">{{ devicesError }}</div>
              <button class="retry-button" @click="fetchAvailableDevices">Retry</button>
            </div>

            <!-- Table Rows -->
            <div v-else>
              <div
                  v-for="device in paginatedDevices"
                  :key="device.logical_device_number"
                  class="table-row meter-row"
                  :class="{ 'selected': isDeviceSelected(device) }"
                  @click="handleDeviceClick($event, device)"
              >
                <div class="table-cell">
                  {{ device.logical_device_number }}
                </div>
                <div class="table-cell">{{ device.active_mpid || '-' }}</div>
                <div class="table-cell">{{ device.metering_point?.city || '-' }}</div>
                <div class="table-cell">{{ device.metering_point?.street || '-' }}</div>
                <div class="table-cell">
                  <span v-if="device.days_since_last_reading === -1" style="color: #ff8a80;">
                    Never
                  </span>
                  <span v-else-if="device.days_since_last_reading !== undefined">
                    {{ device.days_since_last_reading }} days ago
                  </span>
                  <span v-else>-</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="filteredDevices.length > devicesPageSize" class="pagination-controls">
            <div class="pagination-info">
              Showing {{ devicesStartItem }}-{{ devicesEndItem }} of {{ filteredDevices.length }} items
            </div>
            <div class="pagination-buttons">
              <button
                  class="pagination-button"
                  :disabled="devicesCurrentPage === 1"
                  @click="changeDevicesPage(devicesCurrentPage - 1)"
              >
                Previous
              </button>

              <div class="page-numbers">
                <button
                    v-for="page in devicesVisiblePageNumbers"
                    :key="page"
                    :class="['page-number', { active: devicesCurrentPage === page }]"
                    @click="changeDevicesPage(page)"
                >
                  {{ page }}
                </button>
              </div>

              <button
                  class="pagination-button"
                  :disabled="devicesCurrentPage === devicesTotalPages"
                  @click="changeDevicesPage(devicesCurrentPage + 1)"
              >
                Next
              </button>
            </div>
          </div>

          <!-- Create Route Button -->
          <div class="create-route-footer">
            <span class="selected-count">{{ selectedDevices.length }} devices selected</span>
            <button
                class="create-route-button"
                @click="createRoute"
                :disabled="!canCreateRoute || isCreatingRoute"
            >
              {{ isCreatingRoute ? 'Creating...' : 'Create Route' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Active Routes Tab -->
      <div v-else-if="activeTab === 'activeroutes'" class="tab-content">
        <div class="table-wrapper">
          <!-- Active Routes Table -->
          <div class="routes-table">
            <!-- Table Header -->
            <div class="table-header">
              <div class="table-cell">ROUTE NAME</div>
              <div class="table-cell">STATE</div>
              <div class="table-cell">METERS</div>
              <div class="table-cell">GATEWAY</div>
              <div class="table-cell">DRIVER</div>
              <div class="table-cell">ACTIONS</div>
            </div>

            <!-- Loading State -->
            <div v-if="activeRoutesLoading" class="loading-state">
              <div class="loading-spinner"></div>
              <span>Loading active routes...</span>
            </div>

            <!-- No Data State -->
            <div v-else-if="!activeRoutes || activeRoutes.length === 0" class="no-data-message">
              No active routes found.
            </div>

            <!-- Table Rows -->
            <div v-else>
              <div
                  v-for="(route, index) in activeRoutes"
                  :key="route.id || index"
                  class="table-row"
                  @click="selectRoute(route)"
                  :class="{ 'selected': selectedRoute && selectedRoute.id === route.id }"
              >
                <div class="table-cell">{{ route.routeName }}</div>
                <div class="table-cell">
                  <div
                      class="state-icon-wrapper"
                      :class="getStateClass(route.state)"
                      v-html="getStateIcon(route.state)"
                  ></div>
                </div>
                <div class="table-cell">
                  <span :class="['meter-progress', { 'completed': getReadMetersCount(route) === getTotalMetersCount(route) }]">
                    {{ getReadMetersCount(route) }}/{{ getTotalMetersCount(route) }}
                  </span>
                </div>
                <div class="table-cell">{{ route.gateway_name || '-' }}</div>
                <div class="table-cell">{{ route.assigned_driver?.email || '-' }}</div>
                <div class="table-cell">
                  <div class="action-buttons">
                    <!-- Start Route Button (for Active state) - Uses overlay with 'start' mode -->
                    <button
                        v-if="route.state === 'Active'"
                        class="action-button-small"
                        @click="openStartRouteOverlay(route, 'start')"
                        title="Start Route"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                      </svg>
                    </button>

                    <!-- Assign Driver & Gateway Button (for Active state) - Uses overlay with 'assign' mode -->
                    <button
                        v-if="route.state === 'Active'"
                        class="action-button-small"
                        @click="openStartRouteOverlay(route, 'assign')"
                        title="Assign Driver & Gateway"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="8.5" cy="7" r="4"></circle>
                        <line x1="20" y1="8" x2="20" y2="14"></line>
                        <line x1="23" y1="11" x2="17" y2="11"></line>
                      </svg>
                    </button>

                    <!-- Continue Button (for InProgress state) - assign gateway & driver and start -->
                    <button
                        v-if="route.state === 'InProgress'"
                        class="action-button-small"
                        @click="openStartRouteOverlay(route, 'assign')"
                        title="Continue (Assign Driver & Gateway and Start)"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="8.5" cy="7" r="4"></circle>
                        <polygon points="14 8 22 12 14 16 14 8"></polygon>
                      </svg>
                    </button>

                    <!-- Remove Active Route Button (for Active, InProgress, and Finished states) -->
                    <button
                        v-if="['Active', 'InProgress', 'Finished'].includes(route.state)"
                        class="action-button-small delete-button"
                        @click="deleteActiveRoute(route)"
                        title="Delete Route"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>

                    <!-- Finish Route Button (for InProgress state) -->
                    <button
                        v-if="route.state === 'InProgress'"
                        class="action-button-small"
                        @click="finishRoute(route)"
                        title="Finish Route"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </button>

                    <!-- Export CSV Button (for InProgress and Finished states) -->
                    <button
                        v-if="['InProgress', 'Finished'].includes(route.state)"
                        class="action-button-small"
                        @click="exportRouteToCSV(route)"
                        title="Export Route Readings to CSV"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from './AppHeader.vue';
import DriveByRouteMap from './DriveByRouteMap.vue';
import StartDriveByOverlay from './StartDriveByOverlay.vue';

export default {
  name: 'DrivebyPage',
  components: {
    AppHeader,
    DriveByRouteMap,
    StartDriveByOverlay
  },
  data() {
    return {
      activeRoutes: [],
      activeRoutesLoading: false,
      selectedRouteDevices: [],
      selectedRouteDevicesLoading: false,

      // Tabs configuration
      tabs: [
        { id: 'createroute', label: 'CREATE ROUTE' },
        { id: 'routes', label: 'ROUTES' },
        { id: 'activeroutes', label: 'ACTIVE ROUTES' }
      ],
      activeTab: 'createroute',

      // Table columns configuration
      routeColumns: [
        { id: 'routeName', label: 'ROUTE NAME', sortable: true },
        { id: 'creationDate', label: 'CREATION DATE', sortable: true, type: 'date' },
        { id: 'state', label: 'STATE', sortable: true },
        { id: 'numberOfMeters', label: 'NUMBER OF METERS', sortable: true },
        { id: 'action', label: 'ACTIONS', sortable: false }
      ],

      // Routes data
      routes: [],
      selectedRoute: null,

      // Overlay state
      showStartRouteOverlay: false,
      routeToStart: null,
      overlayMode: 'start',

      // Table state
      isLoading: false,
      sortColumn: 'routeName',
      sortDirection: 'asc',

      // Pagination
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,

      // Create Route data
      newRoute: {
        routeName: '',
        state: 'Template'
      },
      availableDevices: [],
      filteredDevices: [],
      selectedDevices: [],
      deviceFilters: {
        city: '',
        daysSinceReading: null
      },

      // Devices state
      devicesLoading: false,
      devicesError: '',
      devicesCurrentPage: 1,
      devicesPageSize: 10,
      isCreatingRoute: false,
      showCityFilter: false,
      showDaysFilter: false
    };
  },
  computed: {
    // Computed property for devices to display on map
    mapDevices() {
      if ((this.activeTab === 'routes' || this.activeTab === 'activeroutes') && this.selectedRoute) {
        return this.selectedRouteDevices;
      } else if (this.activeTab === 'createroute') {
        return this.availableDevices;
      }
      return [];
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
    },
    startItem() {
      return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    },
    endItem() {
      return Math.min(this.startItem + this.paginatedRoutes.length - 1, this.totalItems);
    },
    visiblePageNumbers() {
      const maxVisible = 5;
      const half = Math.floor(maxVisible / 2);

      let start = Math.max(1, this.currentPage - half);
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end === this.totalPages) {
        start = Math.max(1, end - maxVisible + 1);
      }

      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    sortedRoutes() {
      if (!this.routes || this.routes.length === 0) return [];

      const sorted = [...this.routes];
      sorted.sort((a, b) => {
        let aVal = this.getColumnValue(a, { id: this.sortColumn });
        let bVal = this.getColumnValue(b, { id: this.sortColumn });

        // Handle numeric values
        if (!isNaN(aVal) && !isNaN(bVal)) {
          aVal = Number(aVal);
          bVal = Number(bVal);
        }

        // Handle date values
        const column = this.routeColumns.find(col => col.id === this.sortColumn);
        if (column && column.type === 'date') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        }

        if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });

      return sorted;
    },
    paginatedRoutes() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.sortedRoutes.slice(start, end);
    },

    // Devices pagination
    devicesTotalPages() {
      return Math.max(1, Math.ceil(this.filteredDevices.length / this.devicesPageSize));
    },
    devicesStartItem() {
      return this.filteredDevices.length === 0 ? 0 : (this.devicesCurrentPage - 1) * this.devicesPageSize + 1;
    },
    devicesEndItem() {
      return Math.min(this.devicesStartItem + this.paginatedDevices.length - 1, this.filteredDevices.length);
    },
    devicesVisiblePageNumbers() {
      const maxVisible = 5;
      const half = Math.floor(maxVisible / 2);

      let start = Math.max(1, this.devicesCurrentPage - half);
      let end = Math.min(this.devicesTotalPages, start + maxVisible - 1);

      if (end === this.devicesTotalPages) {
        start = Math.max(1, end - maxVisible + 1);
      }

      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    paginatedDevices() {
      const start = (this.devicesCurrentPage - 1) * this.devicesPageSize;
      const end = start + this.devicesPageSize;
      return this.filteredDevices.slice(start, end);
    },
    isAllSelected() {
      return this.filteredDevices.length > 0 &&
          this.filteredDevices.every(device => this.isDeviceSelected(device));
    },
    canCreateRoute() {
      return this.newRoute.routeName.trim() !== '' && this.selectedDevices.length > 0;
    }
  },
  mounted() {
    // Fetch routes for the routes tab
    this.fetchRoutes();

    // Fetch available devices immediately since createroute is the default tab
    if (this.activeTab === 'createroute') {
      this.fetchAvailableDevices();
    }
  },
  methods: {
    // Handle devices selected from map
    handleMapDevicesSelected(devices) {
      // Replace the current selection with devices selected from the map
      this.selectedDevices = [...devices];

    },

    setActiveTab(tabId) {
      this.activeTab = tabId;

      // Clear route selection when leaving routes/activeroutes tabs
      if (tabId !== 'routes' && tabId !== 'activeroutes') {
        this.selectedRoute = null;
        this.selectedRouteDevices = [];
      }

      if (tabId === 'createroute' && this.availableDevices.length === 0) {
        this.fetchAvailableDevices();
      } else if (tabId === 'activeroutes') {
        this.fetchActiveRoutes();
      }
    },

    async fetchRoutes() {
      this.isLoading = true;
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/omsroutes`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Error fetching routes: ${response.statusText}`);
        }

        const data = await response.json();

        // Map the response to match our expected format
        this.routes = (data.routes || []).map(route => ({
          id: route._id || route.id,
          routeName: route.route_name || route.routeName,
          creationDate: route.creation_date || route.creationDate,
          state: route.state,
          numberOfMeters: route.meter_readings ? Object.keys(route.meter_readings).length : 0,
          meter_readings: route.meter_readings, // Keep this for route display
          action: 'view'
        }));

        this.totalItems = this.routes.length;
      } catch (error) {
        console.error('Error fetching routes:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchAvailableDevices() {
      this.devicesLoading = true;
      this.devicesError = '';

      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL || '';

        const params = new URLSearchParams();
        if (this.deviceFilters.city) {
          params.append('city', this.deviceFilters.city);
        }
        if (this.deviceFilters.daysSinceReading) {
          params.append('days_since_reading', this.deviceFilters.daysSinceReading);
        }

        const url = `${apiBaseUrl}/omsroutes/devicesosm/filtered/all${params.toString() ? '?' + params.toString() : ''}`;

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Error fetching devices: ${response.statusText}`);
        }

        const data = await response.json();

        this.availableDevices = data.devices || [];
        this.filteredDevices = [...this.availableDevices];


        this.devicesCurrentPage = 1;

      } catch (error) {
        console.error('Error fetching available devices:', error);
        this.devicesError = 'Failed to load available devices. Please try again.';
      } finally {
        this.devicesLoading = false;
      }
    },

    applyFilters() {
      // Debounce the filter application
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.fetchAvailableDevices();
      }, 500);
    },

    clearFilters() {
      this.deviceFilters.city = '';
      this.deviceFilters.daysSinceReading = null;
      this.showCityFilter = false;
      this.showDaysFilter = false;
      this.fetchAvailableDevices();
    },

    focusCityFilter() {
      this.showCityFilter = true;
      this.showDaysFilter = false;
      this.$nextTick(() => {
        this.$refs.cityFilter?.focus();
      });
    },

    focusDaysFilter() {
      this.showDaysFilter = true;
      this.showCityFilter = false;
      this.$nextTick(() => {
        this.$refs.daysFilter?.focus();
      });
    },

    isDeviceSelected(device) {
      return this.selectedDevices.some(d => d.logical_device_number === device.logical_device_number);
    },

    toggleDeviceSelection(device) {
      const index = this.selectedDevices.findIndex(d => d.logical_device_number === device.logical_device_number);
      if (index > -1) {
        this.selectedDevices.splice(index, 1);
      } else {
        this.selectedDevices.push(device);
      }
    },

    handleDeviceClick(event, device) {
      if (event.ctrlKey || event.metaKey) {
        // Ctrl/Cmd click - toggle individual selection
        this.toggleDeviceSelection(device);
      } else if (event.shiftKey && this.selectedDevices.length > 0) {
        // Shift click - select range
        this.selectRange(device);
      } else {
        // Regular click - toggle selection
        this.toggleDeviceSelection(device);
      }
    },

    selectRange(toDevice) {
      // Find the last selected device
      const lastSelected = this.selectedDevices[this.selectedDevices.length - 1];
      const lastIndex = this.filteredDevices.findIndex(d => d.logical_device_number === lastSelected.logical_device_number);
      const toIndex = this.filteredDevices.findIndex(d => d.logical_device_number === toDevice.logical_device_number);

      if (lastIndex !== -1 && toIndex !== -1) {
        const start = Math.min(lastIndex, toIndex);
        const end = Math.max(lastIndex, toIndex);

        // Add all devices in the range
        for (let i = start; i <= end; i++) {
          const device = this.filteredDevices[i];
          if (!this.isDeviceSelected(device)) {
            this.selectedDevices.push(device);
          }
        }
      }
    },

    selectAllDevices() {
      this.selectedDevices = [...this.filteredDevices];
    },

    deselectAllDevices() {
      this.selectedDevices = [];
    },

    changeDevicesPage(page) {
      if (page >= 1 && page <= this.devicesTotalPages) {
        this.devicesCurrentPage = page;
      }
    },

    async createRoute() {
      if (!this.canCreateRoute) return;

      this.isCreatingRoute = true;

      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL || '';

        const selectedDeviceIds = this.selectedDevices.map(device => device.logical_device_number);

        const payload = {
          route_name: this.newRoute.routeName,
          state: this.newRoute.state,
          device_ids: selectedDeviceIds
        };

        const response = await fetch(`${apiBaseUrl}/omsroutes`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error(`Error creating route: ${response.statusText}`);
        }

        await response.json();

        alert(`Route "${this.newRoute.routeName}" created successfully with ${this.selectedDevices.length} devices!`);

        this.newRoute.routeName = '';
        this.newRoute.state = 'Template';
        this.selectedDevices = [];

        this.activeTab = 'routes';
        this.fetchRoutes();

      } catch (error) {
        console.error('Error creating route:', error);
        alert('Failed to create route. Please try again.');
      } finally {
        this.isCreatingRoute = false;
      }
    },

    handleSort(columnId) {
      if (this.sortColumn === columnId) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = columnId;
        this.sortDirection = 'asc';
      }
    },

    getColumnValue(route, column) {
      const value = route[column.id];

      if (column.type === 'date' && value) {
        return this.formatDateTime(value);
      }

      if (column.id === 'state') {
        return value || 'UNKNOWN';
      }

      return value || '-';
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return '-';
      }
    },

    selectRoute(route) {
      this.selectedRoute = route;
      this.selectedRouteDevices = [];
      if (route) {
        this.buildRouteDevices(route);
      }
    },
    buildRouteDevices(route) {
      if (!route || !route.meter_readings) {
        this.selectedRouteDevices = [];
        return;
      }

      const deviceIds = Object.keys(route.meter_readings);
      if (deviceIds.length === 0) {
        this.selectedRouteDevices = [];
        return;
      }

      // Build device list directly from route's meter_readings — no API call needed
      this.selectedRouteDevices = deviceIds.map(ldn => {
        const meter = route.meter_readings[ldn];
        return {
          logical_device_number: ldn,
          active_mpid: meter.mpid || '',
          metering_point: {
            lat: meter.lat,
            lon: meter.lon,
            street: meter.street || '',
            city: meter.city || ''
          },
          days_since_last_reading: -1
        };
      }).filter(d => d.metering_point.lat && d.metering_point.lon);
    },

    async activateRoute(route) {
      if (route.state !== 'Template') {
        alert('Route must be in Template state to activate');
        return;
      }

      if (!confirm(`Are you sure you want to activate route "${route.routeName}"?`)) {
        return;
      }

      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/omsroutes/${route.id}/activate`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          let errorMessage = `Error activating route: ${response.statusText}`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorMessage;
          } catch (e) {
            // If response is not JSON, use the default error message
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();

        alert(`Route "${route.routeName}" has been activated successfully! Created active route with ${result.count || 0} devices.`);

        // Refresh both routes and active routes to show the new active route
        this.fetchRoutes();

        // If we're currently viewing active routes, refresh that tab too
        if (this.activeTab === 'activeroutes') {
          this.fetchActiveRoutes();
        }

      } catch (error) {
        console.error('Error activating route:', error);
        alert(error.message || 'Failed to activate route. Please try again.');
      }
    },

    async fetchActiveRoutes() {
      this.activeRoutesLoading = true;
      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/omsroutes/active`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Error fetching active routes: ${response.statusText}`);
        }

        const data = await response.json();

        // Map the data to consistent field names
        this.activeRoutes = (data.routes || []).map(route => ({
          ...route,
          routeName: route.route_name || route.routeName,
          creationDate: route.activation_date || route.creation_date
        }));
      } catch (error) {
        console.error('Error fetching active routes:', error);
      } finally {
        this.activeRoutesLoading = false;
      }
    },

    async deleteRoute(route) {
      if (!confirm(`Are you sure you want to delete route "${route.routeName}"?`)) {
        return;
      }

      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/omsroutes/${route.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Error deleting route: ${response.statusText}`);
        }

        // Clear selected route if it's the one being deleted
        if (this.selectedRoute && this.selectedRoute.id === route.id) {
          this.selectedRoute = null;
        }

        // Refresh routes list
        this.fetchRoutes();

      } catch (error) {
        console.error('Error deleting route:', error);
        alert('Failed to delete route. Please try again.');
      }
    },

    async deleteActiveRoute(route) {
      // Allow deletion for Active, InProgress, and Finished states based on backend logic
      const allowedStates = ['Active', 'InProgress', 'Finished'];
      if (!allowedStates.includes(route.state)) {
        alert(`Cannot delete route in ${route.state} state. Only routes in Active, InProgress, or Finished state can be deleted.`);
        return;
      }

      // Different confirmation messages based on state
      let confirmMessage;
      if (route.state === 'InProgress') {
        confirmMessage = `Are you sure you want to delete the route "${route.routeName}"? This route is currently in progress and will be terminated.`;
      } else {
        confirmMessage = `Are you sure you want to remove the active route "${route.routeName}"? This will delete the active instance but keep the template.`;
      }

      if (!confirm(confirmMessage)) {
        return;
      }

      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/omsroutes/active/${route._id || route.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Error deleting active route: ${response.statusText}`);
        }

        alert(`Active route "${route.routeName}" has been removed successfully!`);

        // Refresh active routes list
        this.fetchActiveRoutes();

        // Also refresh template routes as the template should now be back to "Template" state
        this.fetchRoutes();

      } catch (error) {
        console.error('Error deleting active route:', error);
        alert(error.message || 'Failed to delete active route. Please try again.');
      }
    },

    // Methods for StartDriveByOverlay
    openStartRouteOverlay(route, mode = 'start') {
      this.routeToStart = route;
      this.overlayMode = mode;
      this.showStartRouteOverlay = true;
    },

    closeStartRouteOverlay() {
      this.showStartRouteOverlay = false;
      this.routeToStart = null;
      this.overlayMode = 'start';
    },

    handleRouteStarted() {
      // Refresh the active routes list to show the updated state
      this.fetchActiveRoutes();

      // Close the overlay
      this.closeStartRouteOverlay();
    },

    // Placeholder for finish route functionality
    async finishRoute(route) {
      if (!confirm(`Are you sure you want to finish route "${route.routeName}"?`)) {
        return;
      }

      try {
        const apiBaseUrl = process.env.VUE_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/omsroutes/active/${route._id || route.id}/state`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            state: 'Finished'
          })
        });

        if (!response.ok) {
          throw new Error(`Error finishing route: ${response.statusText}`);
        }

        alert(`Route "${route.routeName}" has been finished successfully!`);
        this.fetchActiveRoutes();

      } catch (error) {
        console.error('Error finishing route:', error);
        alert('Failed to finish route. Please try again.');
      }
    },

    getStateIcon(state) {
      const icons = {
        'Template': `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" title="Template">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="9" y1="9" x2="15" y2="9"></line>
      <line x1="9" y1="13" x2="15" y2="13"></line>
      <line x1="9" y1="17" x2="13" y2="17"></line>
    </svg>
  `,
        'Active': `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" title="Active">
      <circle cx="12" cy="12" r="10"></circle>
      <rect x="9" y="8" width="2" height="8" fill="currentColor"></rect>
      <rect x="13" y="8" width="2" height="8" fill="currentColor"></rect>
    </svg>
  `,
        'InProgress': `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" title="In Progress">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
  `,
        'Finished': `
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" title="Finished">
      <circle cx="12" cy="12" r="10"></circle>
      <polyline points="9 11 12 14 16 10" stroke-width="3"></polyline>
    </svg>
  `
      };

      return icons[state] || icons['Template'];
    },
    getStateClass(state) {
      const classes = {
        'Template': 'state-template',
        'Active': 'state-active',
        'InProgress': 'state-progress',
        'Finished': 'state-finished'
      };

      return classes[state] || 'state-template';
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    // Helper methods for meter reading progress
    getTotalMetersCount(route) {
      if (!route || !route.meter_readings) return 0;
      return Object.keys(route.meter_readings).length;
    },

    getReadMetersCount(route) {
      if (!route || !route.meter_readings) return 0;

      let readCount = 0;
      const meterReadings = route.meter_readings;

      for (const meterId in meterReadings) {
        const meter = meterReadings[meterId];
        // Check if the meter has been read (current_reading is not null)
        if (meter.readings && meter.readings.current_reading !== null) {
          readCount++;
        }
      }

      return readCount;
    },
    exportRouteToCSV(route) {
      if (!route || !route.meter_readings) {
        alert('No readings available to export');
        return;
      }

      try {
        // CSV Header
        const csvRows = [];
        csvRows.push(['ID', 'Value', 'Date', 'Read By'].join(','));

        // Process each meter in meter_readings
        const meterReadings = route.meter_readings;
        for (const meterId in meterReadings) {
          const meter = meterReadings[meterId];
          const readings = meter.readings;

          // Only export meters that have been read (current_reading is not null)
          if (readings && readings.current_reading !== null) {
            // Format the date
            let dateStr = 'N/A';
            if (readings.date_current_reading) {
              try {
                let dateValue;
                // Handle MongoDB date format
                if (readings.date_current_reading.$date) {
                  if (typeof readings.date_current_reading.$date === 'string') {
                    dateValue = new Date(readings.date_current_reading.$date);
                  } else if (readings.date_current_reading.$date.$numberLong) {
                    dateValue = new Date(parseInt(readings.date_current_reading.$date.$numberLong));
                  }
                } else {
                  dateValue = new Date(readings.date_current_reading);
                }

                if (dateValue && !isNaN(dateValue.getTime())) {
                  // Format as DD/MM/YYYY HH:MM:SS
                  const day = String(dateValue.getDate()).padStart(2, '0');
                  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
                  const year = dateValue.getFullYear();
                  const hours = String(dateValue.getHours()).padStart(2, '0');
                  const minutes = String(dateValue.getMinutes()).padStart(2, '0');
                  const seconds = String(dateValue.getSeconds()).padStart(2, '0');
                  dateStr = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
                }
              } catch (e) {
                console.error('Error formatting date:', e);
                dateStr = 'Invalid Date';
              }
            }

            // Escape values for CSV (wrap in quotes if they contain commas)
            const id = this.escapeCSVValue(meterId);
            const value = this.escapeCSVValue(readings.current_reading);
            const date = this.escapeCSVValue(dateStr);
            const readBy = this.escapeCSVValue(readings.readed_by || 'N/A');

            csvRows.push([id, value, date, readBy].join(','));
          }
        }

        // Check if there are any readings to export
        if (csvRows.length === 1) {
          alert('No readings available to export');
          return;
        }

        // Create CSV content
        const csvContent = csvRows.join('\n');

        // Create a Blob from the CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        // Create download link
        const link = document.createElement('a');
        if (link.download !== undefined) {
          // Generate filename with route name and timestamp
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
          const routeName = (route.route_name || route.routeName || 'route').replace(/[^a-z0-9]/gi, '_');
          const filename = `${routeName}_readings_${timestamp}.csv`;

          // Create URL and trigger download
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', filename);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

        }
      } catch (error) {
        console.error('Error exporting CSV:', error);
        alert('Failed to export CSV file. Please try again.');
      }
    },

    /**
     * Escape CSV values (wrap in quotes if needed)
     * @param {*} value - Value to escape
     * @returns {string} - Escaped value
     */
    escapeCSVValue(value) {
      if (value === null || value === undefined) {
        return '';
      }

      const stringValue = String(value);

      // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    }
  }
};
</script>


<style scoped>
/* Remove the old .bg-section styles since it's now a component */
.driveby-page {
  min-height: 100vh;
  color: white;
  font-family: 'Inter', sans-serif;
  position: relative;
}

/* The map component takes the bg-section class */
.bg-section {
  position: absolute;
  top: 0;
  left: 50%;
  width: 50%;
  height: 100%;
  z-index: 0;
  background: linear-gradient(to right,
  rgba(17, 17, 17, 1) 0%,
  rgba(17, 17, 17, 0.95) 45%,
  rgba(17, 17, 17, 0.7) 50%,
  transparent 55%
  );
}

.content {
  position: absolute;
  top: 0;
  left: 0;
  width: 50%; /* limit content to the left half */
  height: 100%;
  padding: 100px 110px;
  z-index: 1;
  overflow-y: auto; /* Allow scrolling if needed */
  box-sizing: border-box;
}

/* Rest of your existing styles remain the same */
.search-container {
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.15em;
  margin-bottom: 24px;
  text-transform: uppercase;
  text-align: left;
}

/* Search Column */
.search-column {
  flex: 1;
}

.search-options {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  padding-left: 25px;
}

.search-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
  position: relative;
  display: flex;
  align-items: center;
}

.search-option:hover,
.search-option.active {
  opacity: 1;
}

.search-input-container {
  position: relative;
  max-width: 300px;
}

.search-input {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 0;
  font-size: 12px;
}

.search-input:focus {
  outline: none;
  border-bottom-color: rgba(255, 255, 255, 0.6);
}

.search-button {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: white;
  cursor: pointer;
}

/* Filters Column */
.filters-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.filter-sections {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-left: 25px;
}

.main-filters {
  display: flex;
  justify-content: start;
  gap: 24px;
  margin-bottom: 16px;
  width: 100%;
}

.filter-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
  position: relative;
  display: flex;
  align-items: center;
}

.filter-option:hover,
.filter-option.active {
  opacity: 1;
}

/* Advanced filter indicator dot */
.filter-indicator {
  display: inline-block;
  width: 6px;
  height: 6px;
  background-color: #4fc3f7;
  border-radius: 50%;
  margin-left: 6px;
  cursor: pointer;
}

.filter-option.has-advanced-filters::after {
  content: '';
}

.sub-filters {
  display: flex;
  justify-content: start;
  gap: 32px;
  width: 100%;
  margin-top: 0;
  padding-bottom: 16px;
}

.sub-filter-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.05em;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
}

.sub-filter-option.disabled {
  opacity: 0.2;
  cursor: not-allowed;
}

.sub-filter-option:hover,
.sub-filter-option.active {
  opacity: 1;
}

/* Actions Column */
.actions-column {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 16px;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.plus-icon {
  font-size: 16px;
  font-weight: bold;
}

/* Tabs Navigation */
.tabs-container {
  margin-bottom: 40px;
  max-width: 50%;
}

.tabs-nav {
  display: flex;
  gap: 40px;
  padding-bottom: 16px;
}

.tab-button {
  background: none;
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
  position: relative;
}

.tab-button:hover {
  opacity: 0.7;
}

.tab-button.active {
  opacity: 1;
}

.tab-button.active::after {
  content: '';
  position: absolute;
  bottom: -17px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #4fc3f7;
}

/* Tab Content */
.tab-content {
  width: 100%; /* Ensure tab-content respects the width of .content */
  box-sizing: border-box;
  overflow-y: auto; /* In case content exceeds available height */
  max-height: 100%; /* Prevent overflow vertically */
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Table Wrapper */
.table-wrapper {
  width: 100%; /* Take full width of tab-content */
  max-width: 100%; /* Don't exceed parent width */
  padding-right: 20px;
  box-sizing: border-box; /* Include padding in width calculation */
  overflow: hidden; /* Prevent overflow */
}

/* Routes Table */
.routes-table {
  width: 100%;
  margin-top: 24px;
}

.table-header {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.table-header .table-cell {
  cursor: default;
}

.table-header .table-cell.sortable {
  cursor: pointer;
  transition: opacity 0.2s;
}

.table-header .table-cell.sortable:hover {
  opacity: 0.8;
}

.sort-indicator {
  margin-left: 6px;
  font-size: 10px;
  opacity: 0.7;
}

.table-row {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  padding: 16px 0;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.table-row:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.table-row.selected {
  background-color: rgba(255, 255, 255, 0.1);
}

.table-cell {
  flex: 1;
  padding: 0 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-cell:first-child {
  flex: 1.5;
  padding-left: 0;
}

.table-cell:last-child {
  padding-right: 0;
}

/* Action Buttons in Table */
.action-buttons {
  display: flex;
  gap: 8px;
}

.action-button-small {
  background: none;
  border: none;
  color: white;
  padding: 4px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.action-button-small:hover {
  opacity: 1;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 16px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* No Data Message */
.no-data-message {
  text-align: center;
  padding: 60px 0;
  font-size: 14px;
  opacity: 0.6;
}

/* Pagination */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  padding-top: 24px;
}

.pagination-info {
  font-size: 12px;
  opacity: 0.7;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pagination-button {
  background: none;
  border: none;
  color: white;
  font-size: 12px;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
  opacity: 1;
}

.pagination-button:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.page-numbers {
  display: flex;
  gap: 6px;
}

.page-number {
  background: none;
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.page-number:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.1);
}

.page-number.active {
  background-color: rgba(255, 255, 255, 0.2);
  font-weight: 600;
}

/* Create Route Styles */
.filter-value {
  background: rgba(79, 195, 247, 0.2);
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 6px;
  font-size: 10px;
  font-weight: 600;
}

.filter-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.03);
  padding: 0 12px;
  border-radius: 4px;
  max-width: 250px;
}

.filter-input-wrapper svg {
  opacity: 0.4;
  flex-shrink: 0;
}

/* Create Route Bar */
.create-route-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 40px 0 24px 0;
  padding: 16px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.devices-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.devices-count {
  font-size: 12px;
  font-weight: 600;
  opacity: 0.8;
  text-transform: uppercase;
}

.selected-indicator {
  font-size: 12px;
  opacity: 0.6;
}

.selected-indicator .dot {
  color: #4fc3f7;
  margin: 0 4px;
}

.create-route-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.create-route-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
}

.create-route-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Enhanced Table Styles */
.devices-table-container {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
}

.table-header.enhanced {
  background: rgba(255, 255, 255, 0.03);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.devices-table-body {
  max-height: 500px;
  overflow-y: auto;
}

.devices-table-body::-webkit-scrollbar {
  width: 6px;
}

.devices-table-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.02);
}

.devices-table-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.devices-table-body::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}

.table-row.enhanced {
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  transition: all 0.2s ease;
}

.table-row.enhanced:hover {
  background: rgba(255, 255, 255, 0.03);
}

.table-row.enhanced.selected {
  background: rgba(79, 195, 247, 0.08);
}

.header-checkbox {
  cursor: pointer;
}

.cell-value {
  opacity: 0.9;
}

.reading-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.05);
}

.reading-status.never {
  background: rgba(255, 138, 128, 0.1);
  color: #ff8a80;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  text-align: center;
}

.empty-state svg {
  opacity: 0.2;
  margin-bottom: 24px;
}

.empty-state p {
  font-size: 16px;
  margin-bottom: 8px;
  opacity: 0.8;
}

.empty-state span {
  font-size: 13px;
  opacity: 0.5;
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  text-align: center;
}

.error-state svg {
  margin-bottom: 24px;
}

.error-state p {
  font-size: 14px;
  color: #ff8a80;
  margin-bottom: 16px;
}

.retry-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid rgba(255, 138, 128, 0.3);
  color: #ff8a80;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-button:hover {
  background: rgba(255, 138, 128, 0.1);
  border-color: rgba(255, 138, 128, 0.5);
}

/* Pagination Updates */
.pagination-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: white;
  padding: 8px 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
}

/* Route Name Section */
.route-name-section {
  margin-bottom: 40px;
}

.route-name-section .section-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.15em;
  margin-bottom: 24px;
  text-transform: uppercase;
  text-align: left;
}

.route-name-section .search-input-container {
  max-width: 300px;
}

/* Filters Header */
.filters-header {
  margin-bottom: 16px;
}

.filters-header .section-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.15em;
  margin-bottom: 24px;
  text-transform: uppercase;
  text-align: left;
}

.filter-options {
  display: flex;
  gap: 24px;
  padding-left: 0px;
}

.filter-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
  position: relative;
}

.filter-option:hover,
.filter-option.active {
  opacity: 1;
}

/* Filter Inputs */
.filter-inputs {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 40px;
  padding-left: 25px;
}

.clear-filter-button {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.clear-filter-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}


/* Table Actions */
.table-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 20px;
  padding-left: 25px;
}

.devices-count {
  font-size: 12px;
  font-weight: 600;
  opacity: 0.8;
}

.selection-actions {
  display: flex;
  align-content: flex-end;
}

/* Meters Table Styles */
.meters-table {
  width: 100%;
  margin-top: 24px;
  overflow-x: hidden;
}

.meters-table .table-header {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  padding: 10px 0;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  overflow-x: hidden;
}

.meters-table .table-row {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  padding: 16px 0;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.2s;
  overflow-x: hidden;
}

.meters-table .table-row:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.meters-table .table-row.selected {
  background-color: rgba(255, 255, 255, 0.1);
}

.checkbox-cell {
  flex: 0 0 40px;
  display: flex;
  align-items: center;
  padding-left: 8px;
}

/* Create Route Footer */
.create-route-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 40px;
  padding-top: 24px;
}

.selected-count {
  font-size: 12px;
  opacity: 0.7;
}

.create-route-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.create-route-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
}

.create-route-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Coming Soon */
.coming-soon {
  text-align: center;
  padding: 80px 40px;
  font-size: 14px;
  opacity: 0.6;
}

/* Responsive */
@media (max-width: 1400px) {
  .table-wrapper {
    max-width: 60%;
  }

  .tabs-container {
    max-width: 60%;
  }

  .create-route-container {
    max-width: 100%;
  }
}

@media (max-width: 1200px) {
  .content {
    width: 50%;
    padding: 100px 60px;
  }

  .table-wrapper {
    max-width: 100%;
    padding-right: 0;
  }

  .bg-section {
    display: none;
  }
}

@media (max-width: 768px) {
  .content {
    padding: 80px 20px;
  }

  .tabs-container {
    max-width: 100%;
  }

  .tabs-nav {
    gap: 20px;
  }

  .table-cell:nth-child(n+6) {
    display: none;
  }

  .table-header .table-cell:nth-child(n+6) {
    display: none;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 16px;
  }

  .filter-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    max-width: 100%;
  }
}

.route-filters-row {
  display: flex;
  align-items: flex-start;
  gap: 60px;
  margin-bottom: 24px;
}

/* Updated Route Name Section */
.route-name-section {
  flex: 1;
  max-width: 300px;
}

.route-name-section .section-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.15em;
  margin-bottom: 16px;
  text-transform: uppercase;
  text-align: left;
}

.route-name-section .search-input-container {
  max-width: 100%;
}

/* Updated Filters Section */
.filters-section {
  flex: 1;
}

.filters-section .section-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.15em;
  margin-bottom: 16px;
  text-transform: uppercase;
  text-align: left;
}

/* New wrapper for filter controls */
.filter-controls {
  display: flex;
  align-items: center;
  gap: 24px;
  padding-left: 25px;
}

.filter-options {
  display: flex;
  gap: 24px;
}

/* Filter Inputs - now inline with buttons */
.filter-inputs {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-top: 0;
  padding-left: 0;
}

.filter-inputs .search-input-container {
  max-width: 200px;
}

.state-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: all 0.3s ease;
  position: relative;
}

/* Template State - Gray */
.state-template {
  background: rgba(158, 158, 158, 0.1);
  border: 1px solid rgba(158, 158, 158, 0.3);
}

.state-template svg {
  color: #9e9e9e;
}


/* Active State - Blue */
.state-active {
  background: rgba(79, 195, 247, 0.1);
  border: 1px solid rgba(79, 195, 247, 0.3);

}

.state-active svg {
  color: #4fc3f7;
}

.state-active:hover {
  background: rgba(79, 195, 247, 0.2);
  transform: scale(1.1);
}

/* In Progress State - Orange */
.state-progress {
  background: rgba(255, 167, 38, 0.1);
  border: 1px solid rgba(255, 167, 38, 0.3);
}

.state-progress svg {
  color: #ffa726;
}

.state-progress:hover {
  background: rgba(255, 167, 38, 0.2);
  animation-play-state: paused;
  transform: scale(1.1);
}

/* Finished State - Green */
.state-finished {
  background: rgba(102, 187, 106, 0.1);
  border: 1px solid rgba(102, 187, 106, 0.3);
}

.state-finished svg {
  color: #66bb6a;
}

.state-finished:hover {
  background: rgba(102, 187, 106, 0.2);
  transform: scale(1.1);
}

/* Animations */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(79, 195, 247, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(79, 195, 247, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(79, 195, 247, 0);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Optional: Add tooltip on hover */
.state-icon-wrapper {
  position: relative;
}

.state-icon-wrapper::after {
  display: none;
}

.state-icon-wrapper:hover::after {
  opacity: 1;
}

/* Adjust table cell for icon alignment */
.table-cell .state-icon-wrapper {
  margin: 0 auto;
}


</style>